/**
 * Audio engine contract. One AudioContext, one channel strip per audio source:
 *
 *   source node -> sync delay -> [audio filters] -> mono/balance -> fader/mute
 *     -> meter -> (program mix gain) -> master bus -> master fader -> limiter?
 *     -> MediaStreamAudioDestinationNode  (program audio track)
 *   and, per monitoring mode, a tap to the monitor bus -> monitor context -> audio output device.
 */
import type { AudioSettings, AudioStrip, FilterInstance, Id } from '../core/model';
import type { Registry } from '../core/registry';
import type { Logger, Unsubscribe } from '../core/services';

export interface AudioEngineDeps {
  registry: Registry;
  logger: Logger;
  sampleRate?: 44100 | 48000;
}

export interface LevelReading {
  /** Per-channel sample peak over the last meter window, dBFS (-Infinity for silence). */
  peakDb: number[];
  /** Per-channel RMS over the last meter window, dBFS. */
  rmsDb: number[];
  /**
   * True while any sample reached full scale within the last second of audio
   * time (held for 1 s after the last clipping sample). Independent of who reads
   * it or how often; cleared when the context stops running.
   */
  clipped: boolean;
}

export const MASTER_STRIP = 'master';

export interface AudioEngine {
  readonly context: AudioContext;
  /** Loads worklet modules. Must be awaited before any other call. */
  init(): Promise<void>;
  /** Resume the context; must be called from a user gesture (autoplay policy). */
  resume(): Promise<void>;
  getState(): AudioContextState;
  onStateChange(listener: (state: AudioContextState) => void): Unsubscribe;

  /** Mixed program audio as a MediaStream with exactly one audio track. The track is stable. */
  getOutputStream(): MediaStream;
  /** Dedicated post-filter/fader/mute strip tap, before program routing. Caller owns close. */
  openSourceTap(sourceId: Id): { track: MediaStreamTrack; close(): void };

  /**
   * Connect (or replace, or with null disconnect) the audio output of a source.
   * Creates the strip on first use. The engine never disconnects `node` from
   * anything other than its own strip input.
   */
  attachSource(sourceId: Id, node: AudioNode | null): void;
  /** Dedicated current strip-to-master observer. Unsupported filters/delay fail closed. */
  observeProgramRoute?(sourceId: Id): {current(): boolean; observation(): Readonly<import('./program-route').RouteInterval> | undefined; drained(): boolean; close(): void};
  /** Actual post-master PCM/absence, independent of any Native/game source. */
  observeProgramOutput?(): {current():boolean;observation():Readonly<import('./program-route').RouteInterval>|undefined;close():void};
  /** Tear down the strip and its filters. */
  removeSource(sourceId: Id): void;
  setStrip(sourceId: Id, strip: AudioStrip): void;
  /** Reconcile the strip's audio filter chain with these instances (non-audio kinds are ignored). */
  setFilters(sourceId: Id, filters: readonly FilterInstance[]): void;

  /**
   * Program mix: gain 0..1 per source, ramped over rampMs. Sources not listed
   * are ramped to 0. Used for scene activation and transition crossfades;
   * independent of the user's fader and mute.
   */
  setProgramMix(gains: Readonly<Record<Id, number>>, rampMs: number): void;

  applySettings(settings: AudioSettings): void;

  /**
   * Latest meter readings keyed by source id plus MASTER_STRIP. Cheap enough
   * to call every animation frame; returns the same object instance updated in place.
   */
  getLevels(): Readonly<Record<string, LevelReading>>;

  /**
   * Cumulative render quanta processed by the master meter in the audio thread.
   * Advances only while the graph is really being pulled, unlike context.currentTime
   * observed from a throttled main thread.
   */
  getQuantaCount(): number;

  destroy(): Promise<void>;
}
